// ============================================
// OTP VERIFICATION PAGE
// ============================================

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, ArrowLeft } from 'lucide-react';
import { Layout } from '../../components/layout';
import { Button, Card, CardContent, Alert } from '../../components/ui';
import { useAuthStore } from '../../store';
import { ROUTES, ERROR_MESSAGES } from '../../constants';
import styles from './VerifyOtpPage.module.css';

export function VerifyOtpPage() {
  const navigate = useNavigate();
  const { verifyOtp, sendOtp, isLoading, pendingEmail } = useAuthStore();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const email = pendingEmail || sessionStorage.getItem('pending_email') || '';

  useEffect(() => {
    if (!email) {
      navigate(ROUTES.LOGIN);
    }
  }, [email, navigate]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when complete
    if (value && index === 5 && newOtp.every(d => d)) {
      handleSubmit(newOtp.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      handleSubmit(pastedData);
    }
  };

  const handleSubmit = async (code?: string) => {
    const otpCode = code || otp.join('');
    
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    try {
      await verifyOtp(otpCode);
      sessionStorage.removeItem('pending_email');
      navigate(ROUTES.INTRO);
    } catch (err) {
      console.error('OTP verification error:', err);
      setError(ERROR_MESSAGES.OTP_INVALID);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0 || !email) return;
    
    try {
      await sendOtp(email);
      setResendTimer(60);
      setError('');
    } catch (err) {
      console.error('Resend OTP error:', err);
      setError(ERROR_MESSAGES.GENERIC);
    }
  };

  return (
    <Layout maxWidth="sm">
      <div className={styles.container}>
        <Button
          variant="ghost"
          onClick={() => navigate(ROUTES.LOGIN)}
          className={styles.backButton}
        >
          <ArrowLeft size={18} />
          Back
        </Button>

        <div className={styles.header}>
          <div className={styles.iconWrapper}>
            <KeyRound size={32} />
          </div>
          <h1 className={styles.title}>Check your email</h1>
          <p className={styles.subtitle}>
            We sent a 6-digit code to <strong>{email}</strong>
          </p>
        </div>

        <Card variant="elevated">
          <CardContent>
            <div className={styles.form}>
              {error && <Alert variant="error">{error}</Alert>}

              <div className={styles.otpInputs} onPaste={handlePaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => { inputRefs.current[index] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className={styles.otpInput}
                    disabled={isLoading}
                    autoFocus={index === 0}
                  />
                ))}
              </div>

              <Button
                type="button"
                onClick={() => handleSubmit()}
                fullWidth
                isLoading={isLoading}
                size="lg"
                disabled={otp.some(d => !d)}
              >
                Verify Code
              </Button>

              <div className={styles.resend}>
                {resendTimer > 0 ? (
                  <span>Resend code in {resendTimer}s</span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResend}
                    className={styles.resendButton}
                    disabled={isLoading}
                  >
                    Resend code
                  </button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
